import { HttpInterceptorFn, HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthService } from '../services/auth.service';

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);

  // Only send credentials to our own API; never leak them cross-origin.
  if (!req.url.startsWith(environment.baseApiUrl)) {
    return next(req);
  }

  const attachHeaders = (request: HttpRequest<unknown>, csrfToken: string | null) => {
    let headers = request.headers;

    // Mutating requests must carry the double-submit CSRF token. The matching
    // CSRF cookie is sent automatically thanks to withCredentials below.
    if (csrfToken && !['GET', 'HEAD', 'OPTIONS'].includes(request.method)) {
      headers = headers.set('x-csrf-token', csrfToken);
    }

    // withCredentials carries the HttpOnly session cookie and the CSRF cookie on every request.
    return request.clone({ headers, withCredentials: true });
  };

  // The CSRF token lives in memory only and is filled in at login, so after a page reload the first
  // mutating request has no token at all. Relying on the 403-retry below for that case means every
  // create/update after a reload burns a guaranteed round-trip and logs a red 403 in the console -
  // so fetch it up front instead, and leave the retry for a token that went stale mid-session.
  const needsToken =
    !['GET', 'HEAD', 'OPTIONS'].includes(req.method) && !authService.getCsrfTokenValue();

  const send = needsToken
    ? authService.fetchCsrfToken().pipe(switchMap((res) => next(attachHeaders(req, res.token))))
    : next(attachHeaders(req, authService.getCsrfTokenValue()));

  return send.pipe(
    catchError((error: HttpErrorResponse) => {
      // CSRF token invalid/missing -> fetch a fresh one and retry once. The
      // retry runs through a fresh next() subscription whose failures land in
      // the inner catchError below, so it can never loop back into here.
      const errString = JSON.stringify(error.error || '').toLowerCase();
      const isCsrfError = error.status === 403 && /\b(csrf|xsrf)\b/.test(errString);

      if (isCsrfError) {
        return authService.fetchCsrfToken().pipe(
          switchMap((res) => next(attachHeaders(req, res.token))),
          catchError((csrfError) => throwError(() => csrfError)),
        );
      }

      // An expired or revoked token is terminal: drop the session and send the
      // user back to /login rather than surfacing a raw 401 per screen. Sign-in
      // itself is excluded — a wrong password 401s, and logging out there would
      // clear the CSRF token the login flow just fetched.
      const isSignIn = /\/auth\/(login|signin|2fa\/login-verify)$/.test(req.url);
      if (error.status === 401 && !isSignIn) {
        authService.expireSession();
      }

      return throwError(() => error);
    }),
  );
};
